/**
 * Temas generales: cómo usar el sistema, panel general, entrada, campana, autorizaciones, impresión, archivos adjuntos,
 * envío por correo, instalar la aplicación y la propia ayuda.
 */
import { SIN_TASA_2, VE_TASA_2, type TemaAyuda } from '../tipos.ts';

export const TEMAS_GENERALES: TemaAyuda[] = [
  {
    id: 'general',
    titulo: 'Cómo usar el sistema',
    resumen: 'Lo básico de MG ERP: dónde está cada cosa, qué puede hacer cada rol y cómo se muestran los precios.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          '**Barra lateral izquierda**: los módulos que tu usuario puede usar. Si entras a uno que no te toca, sale **Sin acceso**.',
          '**Cabecera**: la sucursal y tu caja, la tasa **BCV** del día, la campana de avisos, el botón **Ayuda** y tu menú de usuario.',
          '**Ayuda** (o la tecla **F1**): abre la ayuda de la pantalla en que estás. Un punto ámbar en el botón quiere decir que nunca la abriste en esa pantalla.',
          'En las ventanas con un **?** junto al título, ese botón abre la ayuda de la ventana.',
          'Una **franja roja** debajo de la cabecera avisa de algo que espera por ti (una autorización, un retiro por confirmar).',
          'Los avisos cortos que confirman algo o dicen por qué no se pudo salen **abajo a la izquierda** y se van solos en unos segundos. No tapan la campana, el menú ni el botón **Cobrar**. La **X** los cierra antes.',
          'Cada filtro de una lista lleva su nombre encima (por ejemplo **Tipo**, **Pago**, **Entrega** y **Estado** en Documentos).',
          'Las tablas largas o anchas (libros, reportes, listas) se desplazan dentro de su recuadro, hacia abajo y hacia los lados, con el encabezado fijo arriba: la barra de abajo siempre queda a la vista. Al imprimir salen completas.',
          'Ninguna ventana es más alta que la pantalla: si tiene mucho contenido, se desplaza por dentro.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Desactivar y activar',
        items: [
          '**Desactivar** (un producto, una categoría, una lista de precio, un descuento, un cliente, un usuario, una sucursal, un almacén o una caja) abre primero una ventana que dice qué deja de pasar. Se confirma con **Desactivar** o se vuelve con **Cancelar**.',
          'Nada se borra: lo desactivado queda en su lista, marcado, y se puede activar de nuevo.',
          '**Activar** es directo, sin ventana.',
        ],
      },
      { tipo: 'nota', texto: 'En la cabecera ves además la **Paralela**, que es la tasa 2. Se marca en ámbar cuando lleva más de un día sin escribirse. Los demás usuarios no la ven.', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: 'Cómo se muestran los precios',
        items: [
          'Cada producto tiene **un solo precio, en dólares reales**: lo que paga quien trae efectivo, Zelle o Binance.',
          'En la venta cada precio sale en **Bs**, **$ a BCV** y **$**. El precio **a tasa BCV** es solo una referencia: los bolívares divididos entre la BCV del día.',
          'Al cobrar, la pantalla dice cuánto es si el cliente paga en bolívares y cuánto si paga en dólares (con el **IGTF** en una factura). Cobra lo que dice.',
        ],
        permiso: SIN_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'Cómo se muestran los precios',
        items: [
          'Cada producto tiene **un solo precio, en dólares reales**: lo que paga quien trae efectivo, Zelle o Binance.',
          'Los bolívares salen de multiplicar por la **tasa 2** (la Paralela), que escribes tú.',
          'El precio **a tasa BCV** es solo una referencia: los mismos bolívares divididos entre la BCV del día.',
          'La factura de contado pagada con dólares sale al precio real a tasa BCV y lleva **IGTF** (sección 1.4 del manual).',
          'La tasa 2, el factor y esta regla solo los ves tú. Los demás usuarios ven cuánto se cobra en bolívares y en dólares, sin la tasa ni la explicación.',
        ],
        permiso: VE_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'Reglas que el sistema hace cumplir',
        items: [
          'Un documento emitido (factura, nota, nota de crédito, pago, cierre) **no se edita ni se borra**: se corrige con otro documento.',
          'Cada documento guarda las tasas del momento. Cambiar la tasa después no altera lo ya emitido.',
          '**Toda venta va a un cliente identificado.** Consumidor Final sirve solo para ver precios y cotizar.',
          'Toda acción queda en **Auditoría** con usuario, fecha, hora y el antes y el después.',
        ],
      },
      { tipo: 'nota', texto: 'Si algo pide **Requiere autorización**, no es un error: quien tiene el permiso lo aprueba con su clave o a distancia.' },
    ],
    manual: '1',
    relacionados: ['autorizacion', 'notificaciones', 'adjuntos', 'correo', 'ayuda', 'instalar'],
    claves: ['menú', 'cabecera', 'tasa 2', 'paralela', 'precio', 'roles', 'sin acceso'],
  },
  {
    id: 'inicio',
    titulo: 'Panel general',
    rutas: ['/'],
    resumen: 'Lo primero que ves al entrar: tu caja y las cifras del día que te tocan, cada una con su enlace **Ver**.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Sin caja abierta** (cajero): el aviso «Aún no tienes caja abierta» con el botón **Abrir caja**.',
          '**Con caja abierta**: una tarjeta con tu caja y el botón **Ir a Caja**. No muestra lo recaudado: al cerrar se cuenta a ciegas.',
          '**Ventas de hoy**, **Por entregar** y **Retenciones por recibir** (con cuántas tienen más de 30 días), según tus permisos.',
        ],
      },
      { tipo: 'nota', texto: 'La dueña sin sucursal elegida va directo al **Consolidado**.' },
    ],
    manual: '2',
    relacionados: ['caja', 'general'],
    claves: ['inicio', 'tablero', 'resumen'],
  },
  {
    id: 'login',
    titulo: 'Entrar al sistema',
    rutas: ['/login'],
    resumen: 'Cada persona entra con su **Correo** y su **Contraseña**. La sucursal y el rol vienen con el usuario: no se eligen al entrar.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Tras 5 intentos fallidos el usuario queda bloqueado 15 minutos.',
          'Si olvidaste tu contraseña, la dueña te pone una nueva en **Configuración**, con el botón **Clave** de tu usuario (eso también te desbloquea).',
          'Para salir: tu menú de usuario (arriba a la derecha) y **Cerrar sesión**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Usuario bloqueado por 15 min tras … intentos fallidos', que_hacer: 'Espera los 15 minutos, o pídele a la dueña una clave nueva.' },
      { mensaje: 'Sin acceso', que_hacer: 'Tu usuario no tiene el permiso de esa pantalla: debajo dice cuál, con el nombre que sale en **Permisos**. Pídeselo a la dueña.' },
      { mensaje: 'Tu usuario no tiene el permiso «…». Si lo necesitas, pídeselo a la dueña.', que_hacer: 'Esa operación pide un permiso que no tienes; el mensaje lo nombra. Pídeselo a la dueña.' },
      { mensaje: 'Tu acceso cambió (clave, rol o sucursal): entra de nuevo', que_hacer: 'Te pusieron una clave nueva, o cambió tu rol o tu sucursal. Entra de nuevo; si no tienes la clave nueva, pídesela a la dueña.' },
    ],
    manual: '1.1',
    claves: ['contraseña', 'clave', 'bloqueado', 'olvidé'],
  },
  {
    id: 'notificaciones',
    titulo: 'Avisos y campana',
    resumen: 'La **campana** de la cabecera guarda los avisos del sistema; los que necesitan que hagas algo saltan además en una franja roja y suenan.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La campana',
        items: [
          'Se actualiza sola cada 15 segundos. Un clic en un aviso lo marca leído y te lleva a donde se resuelve.',
          '**Marcar todas leídas** limpia el contador.',
          'Entre otros, aquí llegan el aviso al cajero de que su caja se reabrió y, a los administradores y a la dueña, el de un producto que bajó del mínimo.',
          'A la dueña le llegan los ajustes por aprobar («… propuso el ajuste …», también el de una existencia inicial) y los conteos con diferencias («El conteo de … dejó el ajuste … por aprobar»). Al resolverlos, quien lo propuso recibe «Ajuste … aprobado» o «Ajuste … rechazado».',
          'Una autorización pedida a distancia dice de qué sucursal es: «… (Barinas) pide autorización: …».',
          'Los avisos llegan solo a usuarios activos.',
          'Para callar el sonido, abre la campana y pulsa **con sonido**: pasa a **en silencio** (se guarda en ese equipo).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La franja roja',
        items: [
          'Salta con una autorización pedida a distancia, un retiro de caja por confirmar, una caja que superó el límite de efectivo o el talonario fiscal casi agotado.',
          '**Ver** lleva a la pantalla donde se resuelve; **Entendido** lo quita de la franja.',
          'La autorización y el retiro se apagan solos cuando alguien los resuelve. El de la caja que superó el límite se apaga solo cuando la caja vuelve a estar por debajo del límite (con el retiro) o cuando se cierra.',
          'Si no suena, haz clic una vez en cualquier parte de la página: el navegador no deja sonar antes.',
        ],
      },
      { tipo: 'nota', texto: 'Los avisos son internos: no le llegan al cliente. Al cliente solo le llega por correo el documento que tú le envías con **Enviar por correo**.' },
    ],
    manual: '27',
    relacionados: ['autorizaciones', 'correo'],
    claves: ['campana', 'sonido', 'franja roja', 'aviso'],
  },
  {
    id: 'autorizacion',
    titulo: 'Requiere autorización',
    resumen: 'Algunas operaciones solo las hace quien tiene el permiso. Si las intentas sin él, el sistema abre **Requiere autorización** para que alguien que puede la apruebe.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Con clave, en la misma pantalla',
        pasos: ['Quien tiene el permiso escribe su correo (**Correo de quien autoriza**) y su contraseña.', 'Pulsa **Autorizar con clave**. La operación sigue sola.'],
      },
      { tipo: 'nota', texto: 'La ventana dice a quién pedírsela, según quién tiene hoy ese permiso en tu sucursal: «la dueña» (por ejemplo, para devolver dinero), «un administrador o la dueña», o «la dueña o un usuario con ese permiso».' },
      {
        tipo: 'pasos',
        titulo: 'A distancia',
        pasos: ['Pulsa **Pedir a distancia**.', 'La solicitud le llega a quien puede aprobarla, que responde desde **Autorizaciones**.', 'La pantalla espera sola y sigue al aprobarse.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pide autorización',
        items: ['Descuento en una venta, crédito por encima del límite y venta a un cliente con facturas vencidas.', 'Venta contra un depósito.', 'Anular y reemitir un documento.', 'Devolución fuera de plazo y devolución de dinero.', 'Cierre de caja con diferencia.', 'Retiro en otra sucursal: hacer, extender o anular la orden de retiro y darle un código nuevo.'],
      },
    ],
    mensajes: [
      { mensaje: '… requiere autorización (…): pídela con la clave de …, o a distancia', que_hacer: 'Usa esta ventana con la clave de quien nombra el mensaje («de la dueña», «de un administrador o de la dueña» o «de la dueña o de otro usuario con ese permiso»), o pídela a distancia.' },
      { mensaje: 'No puedes autorizarte a ti mismo', que_hacer: 'La da otra persona con el permiso.' },
      { mensaje: '… no pertenece a esta sucursal', que_hacer: 'La da alguien de la misma sucursal, o la dueña.' },
      { mensaje: 'Esa autorización ya se usó en otra operación: pide una nueva', que_hacer: 'Cada autorización sirve una sola vez: pide otra.' },
      { mensaje: 'La autorización venció: pide una nueva', que_hacer: 'Pasó el tiempo de validez: pide otra.' },
      { mensaje: 'La autorización de … venció sin usarse: pide una nueva.', que_hacer: 'La aprobaron, pero la operación no se hizo a tiempo: pide otra.' },
      { mensaje: 'La autorización es por $ … y la operación es por $ …: pide una nueva', que_hacer: 'La operación terminó siendo por más de lo aprobado: pide otra por el monto correcto.' },
    ],
    manual: '1.7',
    relacionados: ['autorizaciones'],
    claves: ['clave', 'permiso', 'a distancia', 'aprobar'],
  },
  {
    id: 'impresion',
    titulo: 'Imprimir',
    rutas: ['/imprimir/:tipo/:id', '/imprimir/COMPROBANTE/:id/:cid', '/imprimir/PRUEBA/:sucursalId', '/imprimir/REPORTE/igtf'],
    resumen: 'Todo se imprime desde el navegador: el formato se abre en otra pestaña con el cuadro de impresión. Esta ayuda no sale en el papel.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Imprimir',
        pasos: ['En el cuadro de impresión elige la impresora y el tamaño de papel de ese formato.', 'Deja la escala al 100 % y sin márgenes de más.', 'Pulsa **Imprimir**. Cierra la pestaña al terminar.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué papel lleva cada formato',
        items: [
          'Factura, nota de crédito y nota de débito: el **papel preimpreso** de la imprenta (carta en Barquisimeto; en Barinas, la media carta en la mitad de arriba de una hoja carta de pie, que se corta, u horizontal con los talonarios viejos). El sistema imprime solo el cuerpo.',
          'Nota de entrega, Cambio y Cotización: hoja en blanco, con el membrete de los dos logos.',
          'Despacho: media carta vertical o tiquera de 80 mm, según la caja.',
          'Recibo de abono, guía de traslado y comprobante de retiro: media carta vertical.',
          'Comprobante de retención y cierre de caja: hoja carta en blanco.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Membrete y hora',
        items: [
          'La nota de entrega, el Cambio, la cotización y el despacho en media carta llevan en el membrete las dos tiendas, cada una con su teléfono fijo y su WhatsApp. Los números los carga la dueña en **Configuración**, con **Datos y teléfonos** de cada sucursal.',
          'La tiquera de 80 mm no lleva los teléfonos: sale con los logos, la razón social, el RIF y la sucursal.',
          'La factura, la nota de crédito y la nota de débito traen debajo de **Cond. de Pago** la **Hora Emisión**: la hora en que se emitió. La nota de entrega y el Cambio también la traen, debajo de la condición de pago. Al reimprimir sale la misma hora, no la de la impresión.',
        ],
      },
      { tipo: 'nota', texto: 'Si el cuerpo de la factura no cae en el hueco del papel preimpreso, la dueña ajusta los márgenes del papel de factura en **Configuración**.' },
      { tipo: 'nota', texto: 'Cada impresión queda en la auditoría. Desde la segunda, el despacho, el cierre de caja, el recibo de abono y el comprobante de retención dicen **REIMPRESIÓN** (el comprobante, con su número de impresión). La factura reimpresa sale igual que el original: el aviso **REIMPRESIÓN** solo sale en la pantalla.' },
    ],
    manual: '28',
    relacionados: ['correo', 'instalar'],
    claves: ['impresora', 'papel', 'márgenes', 'tiquera', 'reimprimir', 'membrete', 'teléfono', 'WhatsApp', 'hora de emisión'],
  },
  {
    id: 'adjuntos',
    titulo: 'Archivos adjuntos',
    resumen: 'Una foto o un PDF guardado en el sistema junto a lo que respalda: la factura del proveedor de una compra, el recibo de un gasto, el comprobante de una retención, el comprobante del pago de un reintegro, los papeles del cliente o el soporte de un documento.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Adjuntar un archivo',
        pasos: [
          'En la sección de archivos (por ejemplo **Factura del proveedor y otros archivos** en el detalle de una compra), pulsa **Adjuntar archivo** y elige uno o varios.',
          'En el teléfono, el botón de la cámara, al lado, toma la foto en el momento.',
          'Se guarda al elegirlo: sale «Archivo adjuntado: «…»» y el archivo aparece en la lista con su tipo, su tamaño, quién lo subió y cuándo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué se puede adjuntar',
        items: [
          'Fotos (JPG, PNG o WEBP) y PDF, de hasta 3 MB cada uno. Las fotos de más de 600 KB se achican solas antes de guardarse: quedan de 200 a 400 KB y se siguen leyendo.',
          'Una foto del teléfono muy pesada, o en un formato que no se guarda, se convierte sola en un JPG más liviano antes de subirla.',
          'El mismo archivo no se adjunta dos veces a lo mismo.',
          'Del nombre del archivo se quitan las carpetas y los caracteres invisibles o que lo hacen leer al revés.',
          'Los archivos quedan dentro del sistema, no en una dirección pública. Los ve quien puede ver lo que adjuntan, en su sucursal; los de la ficha del cliente, en todas.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Dónde se adjunta',
        items: [
          '**Compras**: en el detalle de la compra, **Factura del proveedor y otros archivos**.',
          '**Gastos**: el botón del clip en la fila del gasto abre **Recibo y soportes**.',
          '**Retenciones**: la **Imagen del comprobante** al cargarlo; el **Comprobante del pago** al reintegrar; el clip de cada fila y la columna **Pago** del **Historial**.',
          '**Clientes**: en la ficha, **Archivos del cliente** (la foto del RIF, la cédula, una carta).',
          '**Documentos**: en el detalle del documento, **Archivos del documento**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quién adjunta',
        items: [
          'En una compra o un gasto, quien los registra. El contador los ve sin adjuntar.',
          'La imagen del comprobante de una retención, quien carga comprobantes (también el cajero).',
          'El comprobante del pago de un reintegro, quien valida reintegros. La caja lo ve en el **Historial**.',
          'En la ficha del cliente, quien crea clientes. En un documento, quien vende.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Ver y borrar',
        items: [
          'Pulsa el nombre del archivo o **Ver**: se abre en una ventana. Si el PDF no se ve ahí, usa **Abrir en otra pestaña**. Al abrirlo, nada de lo que traiga adentro se ejecuta.',
          'Un archivo no se cambia. Si se adjuntó mal, la dueña lo borra con **Borrar** y su **Motivo** (al menos 5 letras), y se adjunta el bueno.',
          'Lo borrado deja de verse, pero queda guardado. Subir y borrar quedan en la **Auditoría**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'El archivo «…» pesa …; el máximo por archivo es 3 MB. Redúcelo (una foto más pequeña o un PDF comprimido) e inténtalo de nuevo', que_hacer: 'Toma la foto con menos resolución o comprime el PDF y adjúntalo otra vez.' },
      { mensaje: '«…» no se puede adjuntar: solo fotos (JPG, PNG o WEBP) o PDF', que_hacer: 'Adjunta una foto o un PDF. Un documento de Word o una hoja de Excel no se adjuntan: guárdalos como PDF.' },
      { mensaje: '«…» no es un … de verdad: solo se adjuntan fotos (JPG, PNG o WEBP) o PDF', que_hacer: 'El archivo dice ser una foto o un PDF y no lo es. Ábrelo en tu equipo y guárdalo de nuevo como foto o PDF.' },
      { mensaje: 'La foto sigue pesando más de 3 MB: tómala de nuevo con menos resolución', que_hacer: 'Baja la resolución de la cámara o recorta la foto.' },
      { mensaje: 'Ese archivo ya está adjunto: «…», lo subió … el …', que_hacer: 'Ya está en la lista: no hace falta subirlo otra vez.' },
      { mensaje: 'Esta retención no tiene reintegro: el comprobante del pago se adjunta cuando queda pendiente de reintegro o ya se reintegró', que_hacer: 'La imagen del comprobante de la retención va en **Imagen del comprobante**. El comprobante del pago se adjunta cuando la retención tiene reintegro.' },
      { mensaje: 'Solo la dueña borra archivos adjuntos', que_hacer: 'Pídele a la dueña que lo borre, con el motivo.' },
    ],
    manual: '1.8',
    relacionados: ['compras.detalle', 'gastos', 'retenciones', 'clientes-ficha', 'documentos', 'auditoria'],
    claves: ['adjuntar', 'archivo', 'foto', 'PDF', 'soporte', 'comprobante', 'factura del proveedor', 'cámara', 'imagen'],
  },
  {
    id: 'correo',
    titulo: 'Enviar por correo',
    permiso: { clave: 'ventas.emitir' },
    resumen: 'El botón **Enviar por correo** le manda al cliente, a su correo registrado, la factura, la nota de entrega, la nota de crédito o de débito, el Cambio, la cotización, el recibo de abono o el pago de un reintegro, con el mismo formato del papel. El estado del retiro sale solo, y cada correo queda en la sección **Correos**.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Enviar un documento',
        pasos: [
          'Pulsa **Enviar por correo**. Está en el detalle del documento, en la cotización, en el recibo de abono, en **Pagos recibidos** de la ficha del cliente (los abonos) y en la columna **Pago** del **Historial** de Retenciones (los reintegros hechos).',
          'La ventana dice a qué correo va: «Se envía a … (correo registrado de …)», con el asunto, por ejemplo «Factura N° A-000123 · …».',
          'Pulsa **Enviar**. Sale «Correo enviado a …» con el asunto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Va solo al correo de la ficha del cliente: no se escribe otra dirección. Si falta, agrégalo en su ficha con **Editar datos**. Al enviar, el sistema revisa otra vez ese correo: si está mal escrito, no sale.',
          'El mismo documento se envía una vez cada 2 minutos. Cada usuario envía hasta 20 correos cada 10 minutos.',
          'A un cliente le salen a lo sumo **3 correos por día**, a mano y automáticos juntos. La ventana dice cuántos más puede recibir hoy y el aviso de enviado dice cuántos le quedan. Si varias fichas tienen el mismo correo, comparten los 3. La orden de compra al proveedor no cuenta.',
          'Lo envía quien tiene completo el permiso de eso: en las plantillas, la dueña, el administrador y el cajero. El contador no envía correos a los clientes, ni quien solo puede ver el documento: a ellos el botón no les aparece.',
          'El correo trae lo mismo que el papel: el membrete con las tiendas y su WhatsApp, el número, el control si lo tiene, la fecha y la hora, el cliente, los renglones, los totales y las leyendas. Va dentro del correo, no como PDF.',
          'La factura, la nota de crédito y la nota de débito dicen que el correo es su representación digital: el original fiscal es la hoja impresa en el papel de la imprenta.',
          'No se envía lo que está a nombre de Consumidor Final, una hoja anulada por hoja dañada ni una cotización anulada.',
          'El pago de un reintegro se envía después de hecho, con el comprobante del pago que esté adjunto.',
          'Cada envío queda en la **Auditoría**: a quién, con qué asunto y qué archivos llevó.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Estado de su retiro',
        items: [
          'Le dice al cliente, por producto, lo comprado, lo retirado y lo pendiente, dónde retirarlo con los teléfonos de la tienda y el plazo. No lleva precios.',
          'Sale solo después de cada entrega que deja algo pendiente, después de **Dejar pendiente**, al completar un retiro hecho en varias veces y a los 5 días hábiles sin retirar. Si el cliente se lleva todo de una vez, no sale.',
          'Si el cliente no tiene correo, no pasa nada: usa **Copiar mensaje para WhatsApp** y pégalo en su chat.',
          'A mano: **Enviar estado de retiro** en el detalle del documento. Lo envía quien entrega (caja, almacén, administración).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Retiro en otra sucursal',
        items: [
          'Al hacer la orden de retiro, el cliente recibe solo «Retiro autorizado en …» con la dirección, los productos, el código de retiro y hasta cuándo vale.',
          'Si retira en partes, recibe un código nuevo para lo que falta. Si la orden vence o se anula, recibe el aviso.',
          'El código va solo en el correo: la sección **Correos** guarda lo que se informó, sin el código. Estos correos no se reenvían a mano: para otro código, **Código nuevo** en **Órdenes de retiro**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Sección Correos',
        items: [
          'En el detalle de cada documento y en la ficha del cliente, **Correos** lista cada correo que se le envió: la fecha, a quién, quién lo envió (o **Automático**) y si salió.',
          '**No salió** dice el motivo (por ejemplo, el servicio de correo no lo aceptó). Puedes volver a enviarlo a mano.',
          '**Qué decía** muestra el asunto y el resumen: en el estado del retiro, lo comprado, lo retirado y lo pendiente.',
        ],
      },
      { tipo: 'nota', texto: 'Si el botón está apagado, debajo dice por qué: el cliente sin correo, Consumidor Final, un documento anulado o el correo sin activar en el servidor.' },
    ],
    mensajes: [
      { mensaje: '… no tiene correo registrado: agrégalo en su ficha', que_hacer: 'Abre la ficha del cliente, pulsa **Editar datos**, escribe su **Correo** y vuelve a enviar.' },
      { mensaje: 'El correo registrado de … no es válido: corrígelo en su ficha', que_hacer: 'El correo de la ficha está mal escrito (por ejemplo, con comas, espacios o un nombre). Corrígelo con **Editar datos** y vuelve a enviar.' },
      { mensaje: 'Ese documento ya se envió hace … s a …: si hace falta mandarlo otra vez, espera 2 minutos', que_hacer: 'Ya salió. Si hace falta mandarlo otra vez, espera 2 minutos.' },
      { mensaje: 'Enviaste muchos correos en poco tiempo: espera unos minutos', que_hacer: 'Cada usuario envía hasta 20 correos cada 10 minutos. Espera unos minutos y envía de nuevo.' },
      { mensaje: 'Hoy ya se enviaron 3 correos a …, el máximo por día: se puede enviar otra vez mañana', que_hacer: 'A un cliente le salen a lo sumo 3 correos por día. Envíalo mañana o avísale por otra vía.' },
      { mensaje: 'El contador no envía correos a los clientes: lo hace la caja o la administración de la tienda', que_hacer: 'Pídele el envío a la caja o a la administración.' },
      { mensaje: 'Está a nombre de Consumidor final: no se envía por correo. Para enviarlo, debe estar a nombre del cliente con su correo registrado', que_hacer: 'Ese documento no se envía. Las ventas siguientes, a nombre del cliente con su correo.' },
      { mensaje: '… N° … está anulada por hoja dañada: no se envía por correo. La venta sigue en …', que_hacer: 'Envía el documento que nombra el mensaje.' },
      { mensaje: 'La cotización N° … está anulada: no se envía por correo', que_hacer: 'Haz una cotización nueva si el cliente la necesita.' },
      { mensaje: 'La retención de la factura N° … todavía no se ha reintegrado: el correo con el pago se envía después del reintegro', que_hacer: 'Primero se reintegra; después se envía desde la columna **Pago** del **Historial**.' },
      { mensaje: 'El envío por correo no está configurado en el servidor: falta la clave de Resend o el remitente', que_hacer: 'El correo todavía no está activado. Avisa a la dueña.' },
      { mensaje: 'No se pudo enviar el correo: …', que_hacer: 'El servicio de correo no lo aceptó y el mensaje dice por qué. Revisa el correo de la ficha del cliente o inténtalo más tarde.' },
      { mensaje: 'Esta venta no tiene material que retirar', que_hacer: 'Es una venta de servicios o ya se devolvió todo: no hay estado de retiro que enviar.' },
      { mensaje: 'No se pudo copiar el mensaje', que_hacer: 'Lee el motivo. Si el navegador no deja copiar, vuelve a intentarlo con la pantalla en primer plano.' },
    ],
    manual: '1.9',
    relacionados: ['documentos', 'cotizaciones', 'clientes-ficha', 'retenciones.historial', 'pos.abono'],
    claves: ['correo', 'email', 'enviar', 'mandar factura', 'mandar cotización', 'recibo de abono', 'reintegro'],
  },
  {
    id: 'instalar',
    titulo: 'Instalar MG ERP en la PC o el teléfono',
    resumen: 'MG ERP se instala como una aplicación, sin tienda: queda un ícono en el escritorio o en la pantalla de inicio y abre en su propia ventana. Sin internet no funciona.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'En la PC (Chrome o Edge) o en Android',
        pasos: ['Abre tu menú de usuario, arriba a la derecha.', 'Pulsa **Instalar app**.', 'Confirma con **Instalar**. Queda en el escritorio de la PC o en la pantalla de inicio del teléfono.'],
      },
      {
        tipo: 'pasos',
        titulo: 'En iPhone o iPad',
        pasos: ['Abre MG ERP en **Safari**.', 'Toca **Compartir** (el cuadro con la flecha hacia arriba).', 'Elige **Agregar a pantalla de inicio** y toca **Agregar**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          '**Instalar app** sale solo si el navegador permite instalarla. Ya instalada, o en un navegador que no lo ofrece, no sale.',
          'Es el mismo sistema: entras con tu correo y tu contraseña y tienes los mismos permisos.',
          'Las hojas de impresión se abren en otra ventana, igual que en el navegador.',
          'Si debajo de la cabecera sale «Hay una versión nueva: se aplicará al recargar», termina lo que estás haciendo y pulsa **Recargar**. El sistema nunca se recarga solo.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: '**Sin internet no se vende ni se cobra.** Los precios, las existencias y los documentos vienen siempre del servidor: el equipo no guarda datos.' },
    ],
    mensajes: [
      { mensaje: 'Sin conexión: MG ERP necesita internet para vender y cobrar.', que_hacer: 'Revisa el wifi o los datos del teléfono. Cuando vuelve la conexión la página se recarga sola, o pulsa **Reintentar**.' },
      { mensaje: 'Hay una versión nueva: se aplicará al recargar.', que_hacer: 'Termina lo que estás haciendo (por ejemplo, un cobro abierto) y pulsa **Recargar**.' },
    ],
    manual: '1.10',
    relacionados: ['general', 'impresion'],
    claves: ['instalar', 'aplicación', 'app', 'escritorio', 'pantalla de inicio', 'iphone', 'android', 'sin conexión', 'internet', 'versión nueva', 'recargar'],
  },
  {
    id: 'ayuda',
    titulo: 'Ayuda y manual',
    rutas: ['/ayuda'],
    resumen: 'Todos los temas de ayuda que puedes ver, con buscador. En cualquier pantalla, **Ayuda** (o **F1**) abre directamente la de esa pantalla.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Cómo buscar',
        items: ['Escribe una palabra en el buscador: «vuelto», «IGTF», «conteo», «devolución». No importan acentos ni mayúsculas.', 'Pulsa un tema para desplegarlo, con sus pestañas y ventanas.', 'Solo salen los temas de lo que tu usuario puede usar.'],
      },
    ],
    manual: '1.5',
    relacionados: ['general'],
    claves: ['buscar', 'manual'],
  },
];
